// src/components/CookAI/CtaBanner/CtaSection.tsx
import React from "react";
import { ColorTheme } from "../../../themes/colors";
import Reveal from "../common/Reveal";
import { useLanguage } from "../../../i18n/LanguageContext";

const APP_STORE_URL = "https://apps.apple.com/us/app/cook-ai-hey-chef/id6758898666";

interface CtaSectionProps {
    theme: ColorTheme;
    isMobile: boolean;
}

const CtaSection: React.FC<CtaSectionProps> = ({ theme, isMobile }) => {
    const { t } = useLanguage();

    return (
        <section
            style={{
                padding: isMobile ? "3.5rem 1.25rem" : "5.5rem 2rem",
                background: theme.featuresGradient,
            }}
        >
            <Reveal>
                <div
                    style={{
                        maxWidth: "1080px",
                        margin: "0 auto",
                        background: `linear-gradient(120deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                        borderRadius: "32px",
                        padding: isMobile ? "3rem 1.75rem" : "4.5rem 3.5rem",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: `0 28px 64px ${theme.primaryGlow}`,
                    }}
                >
                    {/* Decorative rings */}
                    <div aria-hidden style={{
                        position: "absolute",
                        top: "-110px",
                        left: "-110px",
                        width: "300px",
                        height: "300px",
                        borderRadius: "50%",
                        border: "36px solid rgba(255,255,255,0.10)",
                        pointerEvents: "none",
                    }} />
                    <div aria-hidden style={{
                        position: "absolute",
                        bottom: "-130px",
                        right: "-90px",
                        width: "320px",
                        height: "320px",
                        borderRadius: "50%",
                        border: "28px solid rgba(255,255,255,0.08)",
                        pointerEvents: "none",
                    }} />
                    {/* Floating food emojis */}
                    {!isMobile && ["🥑", "🍳", "🥕", "🍋"].map((emoji, i) => (
                        <span key={i} aria-hidden style={{
                            position: "absolute",
                            fontSize: "2rem",
                            opacity: 0.85,
                            top: i < 2 ? "18%" : "auto",
                            bottom: i >= 2 ? "16%" : "auto",
                            left: i % 2 === 0 ? `${7 + i * 3}%` : "auto",
                            right: i % 2 === 1 ? `${7 + i * 2}%` : "auto",
                            animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                            animationDelay: `${i * 0.4}s`,
                            filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.18))",
                        }}>
                            {emoji}
                        </span>
                    ))}

                    <h2 style={{
                        fontSize: "clamp(2rem, 4.5vw, 3rem)",
                        fontWeight: 800,
                        color: "#FFFFFF",
                        marginBottom: "1rem",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.1,
                        position: "relative",
                    }}>
                        {t.ctaBanner.title}
                    </h2>
                    <p style={{
                        fontSize: "1.1rem",
                        color: "rgba(255,255,255,0.88)",
                        maxWidth: "520px",
                        margin: "0 auto 2.25rem auto",
                        lineHeight: 1.65,
                        position: "relative",
                    }}>
                        {t.ctaBanner.subtitle}
                    </p>

                    <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.6rem",
                            background: "#FFFFFF",
                            color: theme.textPrimary,
                            padding: "1rem 2.25rem",
                            borderRadius: "16px",
                            fontSize: "1.05rem",
                            fontWeight: 700,
                            textDecoration: "none",
                            boxShadow: "0 14px 36px rgba(7, 9, 26, 0.25)",
                            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease",
                            position: "relative",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-3px) scale(1.03)";
                            e.currentTarget.style.boxShadow = "0 22px 48px rgba(7, 9, 26, 0.32)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0) scale(1)";
                            e.currentTarget.style.boxShadow = "0 14px 36px rgba(7, 9, 26, 0.25)";
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 814 1000" fill="currentColor">
                            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.9 0 130.1 46.4 173.4 46.4 42.7 0 109.2-49 188.3-49 30.5 0 108.2 2.6 160.6 61.4zm-244-190.5c31.5-37 53.5-88.1 53.5-139.2 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.5-55.1 135.2 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-71.1z"/>
                        </svg>
                        {t.ctaBanner.button}
                    </a>

                    <div style={{
                        marginTop: "1.1rem",
                        fontSize: "0.85rem",
                        color: "rgba(255,255,255,0.75)",
                        fontWeight: 500,
                        position: "relative",
                    }}>
                        {t.ctaBanner.note}
                    </div>
                </div>
            </Reveal>
        </section>
    );
};

export default CtaSection;
